import type { ReminderIntensity, ReminderMode, ReminderSpec } from '@taskos/shared';
import { Plus, X } from 'lucide-react';
import { useId, useState } from 'react';
import { useReminderPresets } from '../../api/queries';
import { Button, Segmented } from '../../components/ui';

function keyOf(r: Pick<ReminderSpec, 'kind' | 'offsetMinutes'>) {
  return `${r.kind}:${r.offsetMinutes}`;
}

export function describe(r: Pick<ReminderSpec, 'kind' | 'offsetMinutes' | 'at'>): string {
  if (r.kind === 'ABSOLUTE') return `At ${r.at ? new Date(r.at).toLocaleString() : 'a set time'}`;
  if (r.kind === 'AT_DUE') return 'At due time';
  const m = r.offsetMinutes;
  const txt = m % 1440 === 0 && m > 0 ? `${m / 1440} day${m === 1440 ? '' : 's'}` : m % 60 === 0 && m > 0 ? `${m / 60} hour${m === 60 ? '' : 's'}` : `${m} min`;
  return r.kind === 'BEFORE_DUE' ? `${txt} before` : `${txt} after due`;
}

export function ReminderPicker({ value, onChange, hasDue }: { value: ReminderSpec[]; onChange: (v: ReminderSpec[]) => void; hasDue: boolean }) {
  const presets = useReminderPresets();
  const id = useId();
  const [mode, setMode] = useState<ReminderMode>(value[0]?.mode ?? 'ONCE');
  const [intensity, setIntensity] = useState<ReminderIntensity>(value[0]?.intensity ?? 'NORMAL');
  const [repeat, setRepeat] = useState(value[0]?.repeatEveryMinutes ?? 60);
  const [custom, setCustom] = useState({ n: '', unit: '1440', dir: 'BEFORE_DUE' as 'BEFORE_DUE' | 'AFTER_DUE' });
  const selected = new Set(value.map(keyOf));

  const apply = (list: Array<Pick<ReminderSpec, 'kind' | 'offsetMinutes'>>, m = mode, i = intensity, rep = repeat) =>
    onChange(list.map((r) => ({ kind: r.kind, offsetMinutes: r.offsetMinutes, mode: m, intensity: i, repeatEveryMinutes: m === 'ONCE' ? null : rep, at: null })));
  const toggle = (r: Pick<ReminderSpec, 'kind' | 'offsetMinutes'>) => {
    const k = keyOf(r);
    apply(selected.has(k) ? value.filter((x) => keyOf(x) !== k) : [...value, r]);
  };

  if (!hasDue)
    return (
      <div className="inline-alert info small" role="note">
        Reminders need a due date. Pick a date above to add reminders.
      </div>
    );
  const groups: Array<[string, 'QUICK' | 'NORMAL' | 'LONG' | 'AT']> = [
    ['Quick', 'QUICK'],
    ['Normal', 'NORMAL'],
    ['Long', 'LONG'],
    ['Due', 'AT'],
  ];
  return (
    <div className="stack sm" role="group" aria-labelledby={`${id}-l`}>
      <span className="label" id={`${id}-l`}>
        Reminders
      </span>
      {presets.data &&
        groups.map(([name, k]) => (
          <div key={k} className="inline-row">
            <span className="small muted" style={{ width: 52 }}>
              {name}
            </span>
            <div className="chip-row">
              {presets.data[k].map((p) => (
                <button key={p.id} type="button" className="chip" aria-pressed={selected.has(keyOf(p))} onClick={() => toggle(p)}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      <div className="inline-row">
        <span className="small muted" style={{ width: 52 }}>
          Custom
        </span>
        <input className="input" style={{ width: 80 }} type="number" min={1} max={999} inputMode="numeric" aria-label="Custom amount" value={custom.n} onChange={(e) => setCustom({ ...custom, n: e.target.value })} />
        <select className="select" style={{ width: 110 }} aria-label="Unit" value={custom.unit} onChange={(e) => setCustom({ ...custom, unit: e.target.value })}>
          <option value="1">minutes</option>
          <option value="60">hours</option>
          <option value="1440">days</option>
        </select>
        <select className="select" style={{ width: 130 }} aria-label="Before or after" value={custom.dir} onChange={(e) => setCustom({ ...custom, dir: e.target.value as 'BEFORE_DUE' | 'AFTER_DUE' })}>
          <option value="BEFORE_DUE">before due</option>
          <option value="AFTER_DUE">after due</option>
        </select>
        <Button
          size="sm"
          icon={<Plus size={15} />}
          disabled={!(Number(custom.n) >= 1)}
          onClick={() => {
            const off = Number(custom.n) * Number(custom.unit);
            const r = { kind: custom.dir, offsetMinutes: off };
            if (!selected.has(keyOf(r))) apply([...value, r]);
            setCustom({ ...custom, n: '' });
          }}
        >
          Add
        </Button>
      </div>
      {value.length > 0 && (
        <>
          <div className="chip-row" aria-label="Selected reminders">
            {value.map((r) => (
              <span key={keyOf(r)} className="chip active">
                🔔 {describe(r)}
                <button type="button" className="link-btn x" aria-label={`Remove reminder ${describe(r)}`} onClick={() => toggle(r)}>
                  <X size={14} />
                </button>
              </span>
            ))}
          </div>
          <div className="inline-row">
            <Segmented
              label="Reminder mode"
              value={mode}
              onChange={(m) => {
                setMode(m);
                apply(value, m);
              }}
              options={[
                { value: 'ONCE', label: 'Once' },
                { value: 'REPEAT', label: 'Repeat until complete' },
                { value: 'ESCALATE', label: 'Escalate' },
              ]}
            />
            {mode !== 'ONCE' && (
              <label className="inline-row small">
                every
                <select
                  className="select"
                  style={{ width: 110 }}
                  value={repeat}
                  onChange={(e) => {
                    const r = Number(e.target.value);
                    setRepeat(r);
                    apply(value, mode, intensity, r);
                  }}
                >
                  <option value={15}>15 min</option>
                  <option value={30}>30 min</option>
                  <option value={60}>1 hour</option>
                  <option value={180}>3 hours</option>
                  <option value={1440}>1 day</option>
                </select>
              </label>
            )}
          </div>
          <Segmented
            label="Reminder intensity"
            value={intensity}
            onChange={(i) => {
              setIntensity(i);
              apply(value, mode, i);
            }}
            options={[
              { value: 'LOW', label: 'Low' },
              { value: 'NORMAL', label: 'Normal' },
              { value: 'HIGH', label: 'High' },
              { value: 'CRITICAL', label: 'Critical' },
            ]}
          />
        </>
      )}
    </div>
  );
}
